import React, { useEffect, useState, useMemo } from 'react';
import ApiClient from '../helpers/ApiClient';
import { toast } from 'react-hot-toast';
import Select from 'react-select';
import { 
  IconPlus, 
  IconTrash, 
  IconScale, 
  IconCalculator, 
  IconArrowLeft, 
  IconSearch, 
  IconPackage, 
  IconCheck, 
  IconArrowsExchange,
  IconSparkles,
  IconRefresh
} from '@tabler/icons-react';
import Page from "../components/Page";
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useTheme } from '../contexts/ThemeContext';
import { iconStroke } from '../config/config';
import { addInventoryItemStockMovement } from "../controllers/inventory.controller";

export default function UnitConversionsPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const selectCustomStyles = useMemo(() => ({
    control: (base, state) => ({
      ...base,
      backgroundColor: isDark ? '#18181b' : '#ffffff',
      borderColor: state.isFocused ? '#10b981' : isDark ? '#3f3f46' : '#d1d5db',
      borderRadius: '0.75rem',
      minHeight: '38px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(16, 185, 129, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#10b981',
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#18181b' : '#ffffff',
      border: isDark ? '1px solid #3f3f46' : '1px solid #e5e7eb',
      borderRadius: '0.75rem',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
      zIndex: 9999999,
      overflow: 'hidden',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#10b981'
        : state.isFocused
        ? isDark ? '#27272a' : '#f0fdf4'
        : 'transparent',
      color: state.isSelected
        ? '#ffffff'
        : isDark ? '#f4f4f5' : '#1f2937',
      fontSize: '0.875rem',
      padding: '8px 12px',
      cursor: 'pointer',
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#f4f4f5' : '#1f2937',
      fontSize: '0.875rem',
      fontWeight: '500',
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#f4f4f5' : '#1f2937',
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#71717a' : '#9ca3af',
      fontSize: '0.875rem',
    }),
  }), [isDark]);

  const [activeTab, setActiveTab] = useState('mappings'); // 'mappings' or 'calculator'
  const [conversions, setConversions] = useState([]);
  const [purchaseUnits, setPurchaseUnits] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Form states for creating mapping
  const [inventoryItemId, setInventoryItemId] = useState('');
  const [purchaseUnitId, setPurchaseUnitId] = useState('');
  const [conversionFactor, setConversionFactor] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculator states
  const [calcItemId, setCalcItemId] = useState('');
  const [calcUnitId, setCalcUnitId] = useState('');
  const [calcPackCount, setCalcPackCount] = useState('2');
  const [calcPackSize, setCalcPackSize] = useState('750');
  const [calcLooseQty, setCalcLooseQty] = useState('600');
  const [isRecordingStock, setIsRecordingStock] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [convRes, puRes, invRes] = await Promise.all([
        ApiClient.get('/settings/unit-conversions'),
        ApiClient.get('/settings/purchase-units'),
        ApiClient.get('/inventory?status=all')
      ]);

      setConversions(Array.isArray(convRes.data) ? convRes.data : []);
      setPurchaseUnits(Array.isArray(puRes.data) ? puRes.data : []);
      const itemsList = invRes?.data?.items || (Array.isArray(invRes?.data) ? invRes.data : []);
      setInventoryItems(itemsList);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load unit conversion data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    if (e) e.preventDefault();
    if (!inventoryItemId || !purchaseUnitId || !conversionFactor) {
      return toast.error('Please select an item, packaging unit, and enter a conversion factor');
    }
    const factorNum = parseFloat(conversionFactor);
    if (isNaN(factorNum) || factorNum <= 0) {
      return toast.error('Conversion factor must be a positive number');
    }

    setIsSubmitting(true);
    try {
      await ApiClient.post('/settings/unit-conversions', {
        inventory_item_id: inventoryItemId,
        purchase_unit_id: purchaseUnitId,
        conversion_factor: factorNum
      });
      toast.success('Unit conversion mapping saved successfully!');
      setInventoryItemId('');
      setPurchaseUnitId('');
      setConversionFactor('');
      await fetchData();
      try { document.getElementById('modal-add-conversion')?.close(); } catch (err) {}
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to save conversion mapping';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this conversion mapping?')) return;
    try {
      await ApiClient.delete(`/settings/unit-conversions/${id}`);
      toast.success('Conversion mapping deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete conversion mapping');
    }
  };

  // Helper for selected item in modal
  const selectedItemObj = inventoryItems.find(i => String(i.id) === String(inventoryItemId));
  const selectedUnitObj = purchaseUnits.find(u => String(u.id) === String(purchaseUnitId));

  const inventoryItemOptions = useMemo(() => {
    return (inventoryItems || []).map(item => ({
      value: item.id,
      label: `${item.title} (Stock: ${item.quantity || 0} ${item.unit} | Base: ${item.unit})`,
      item: item
    }));
  }, [inventoryItems]);

  const purchaseUnitOptions = useMemo(() => {
    return (purchaseUnits || []).map(pu => ({
      value: pu.id,
      label: `${pu.name} (${pu.symbol}) [${pu.unit_type}]`,
      pu: pu
    }));
  }, [purchaseUnits]);

  // Calculator calculations
  const calcItemObj = inventoryItems.find(i => String(i.id) === String(calcItemId));
  const calcBaseUnit = calcItemObj?.unit || 'unit';

  // Available mappings for calculator item
  const itemConversions = conversions.filter(c => String(c.inventory_item_id) === String(calcItemId));

  // Handle calculator item change
  const handleCalcItemChange = (itemId) => {
    setCalcItemId(itemId);
    const mappings = conversions.filter(c => String(c.inventory_item_id) === String(itemId));
    if (mappings.length > 0) {
      setCalcUnitId(String(mappings[0].purchase_unit_id));
      setCalcPackSize(String(mappings[0].conversion_factor));
    } else {
      setCalcUnitId('custom');
      setCalcPackSize('1');
    }
  };

  // Handle calculator unit change
  const handleCalcUnitChange = (unitId) => {
    setCalcUnitId(unitId);
    if (unitId === 'custom') return;
    const mapping = conversions.find(c => String(c.inventory_item_id) === String(calcItemId) && String(c.purchase_unit_id) === String(unitId));
    if (mapping) {
      setCalcPackSize(String(mapping.conversion_factor));
    }
  };

  const packCountNum = parseFloat(calcPackCount) || 0;
  const packSizeNum = parseFloat(calcPackSize) || 0;
  const looseQtyNum = parseFloat(calcLooseQty) || 0;
  const fullContainersBaseQty = packCountNum * packSizeNum;
  const calculatedTotalStock = fullContainersBaseQty + looseQtyNum;

  // Open calculator pre-filled for a specific mapping
  const openInCalculator = (conversion) => {
    setCalcItemId(String(conversion.inventory_item_id));
    setCalcUnitId(String(conversion.purchase_unit_id));
    setCalcPackSize(String(conversion.conversion_factor));
    setCalcPackCount('2');
    setCalcLooseQty('0');
    setActiveTab('calculator');
  };

  // Apply calculated stock refill directly to inventory
  const handleRecordStockRefill = async () => {
    if (!calcItemId) {
      return toast.error('Please select an inventory item first');
    }
    if (calculatedTotalStock <= 0) {
      return toast.error('Calculated total quantity must be greater than zero');
    }

    const unitLabel = calcUnitId !== 'custom' 
      ? (purchaseUnits.find(u => String(u.id) === String(calcUnitId))?.name || 'Pack')
      : 'Pack';

    const note = `Stock Refill via Unit Conversion: ${packCountNum} ${unitLabel} (${packSizeNum} ${calcBaseUnit}) + ${looseQtyNum} ${calcBaseUnit} loose = ${calculatedTotalStock} ${calcBaseUnit}`;

    setIsRecordingStock(true);
    try {
      await addInventoryItemStockMovement({
        id: calcItemId,
        movementType: 'IN',
        quantity: calculatedTotalStock,
        note
      });
      toast.success(`Successfully refilled ${calculatedTotalStock} ${calcBaseUnit} to ${calcItemObj?.title}!`);
      await fetchData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to record stock movement';
      toast.error(msg);
    } finally {
      setIsRecordingStock(false);
    }
  };

  const filteredConversions = conversions.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (c.inventory_item_name && c.inventory_item_name.toLowerCase().includes(q)) ||
      (c.purchase_unit_name && c.purchase_unit_name.toLowerCase().includes(q)) ||
      (c.purchase_unit_symbol && c.purchase_unit_symbol.toLowerCase().includes(q))
    );
  });

  return (
    <Page className="px-4 py-4 md:px-8 md:py-6">
      {/* Navigation & Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link 
              to="/dashboard/inventory"
              className="text-xs text-restro-green hover:underline flex items-center gap-1 font-semibold"
            >
              <IconArrowLeft size={14} /> Back to Inventory
            </Link>
          </div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-restro-text">
            <IconScale className="text-restro-green" size={28} stroke={iconStroke} />
            Unit Conversions & Packaging Master
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Define multi-unit conversions (e.g. 1 Bottle = 750 ml, 1 Case = 12 Bottles) and calculate compound stock refills automatically.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/dashboard/settings/purchase-units"
            className="text-sm rounded-xl border transition-colors px-3 py-2 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
          >
            <IconPackage size={18} stroke={iconStroke} />
            Manage Packaging Units
          </Link>
          <button 
            onClick={() => document.getElementById('modal-add-conversion')?.showModal()}
            className="text-sm rounded-xl transition-colors px-4 py-2 flex items-center gap-1.5 text-white bg-restro-green hover:bg-restro-green-button-hover font-bold shadow-sm"
          >
            <IconPlus size={18} stroke={iconStroke} />
            Add Conversion Mapping
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-restro-border-green mb-6 gap-2">
        <button
          onClick={() => setActiveTab('mappings')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'mappings'
              ? 'border-restro-green text-restro-green'
              : 'border-transparent text-gray-500 hover:text-restro-text'
          }`}
        >
          <IconArrowsExchange size={18} />
          Conversion Mappings ({conversions.length})
        </button>
        <button
          onClick={() => setActiveTab('calculator')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'calculator'
              ? 'border-restro-green text-restro-green'
              : 'border-transparent text-gray-500 hover:text-restro-text'
          }`}
        >
          <IconCalculator size={18} />
          Stock Take & Packaging Calculator
        </button>
      </div>

      {/* TAB 1: CONVERSION MAPPINGS */}
      {activeTab === 'mappings' && (
        <div className="flex flex-col gap-4">
          {/* Search bar */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative w-full max-w-md">
              <IconSearch className="absolute left-3 top-2.5 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by item name or packaging unit..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full text-sm rounded-xl pl-10 pr-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-green text-restro-text"
              />
            </div>
            <button
              onClick={fetchData}
              title="Refresh"
              className="p-2 rounded-xl border border-restro-border-green bg-restro-gray text-restro-text hover:bg-restro-button-hover"
            >
              <IconRefresh size={18} />
            </button>
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-zinc-900 border border-restro-border-green rounded-2xl overflow-hidden shadow-sm">
            <table className="table table-sm w-full">
              <thead className="bg-restro-bg-gray border-b border-restro-border-green">
                <tr>
                  <th className="py-3 px-4 text-start font-bold">#</th>
                  <th className="py-3 text-start font-bold">Inventory Item</th>
                  <th className="py-3 text-start font-bold">Base Tracking Unit</th>
                  <th className="py-3 text-start font-bold">Packaging / Purchase Unit</th>
                  <th className="py-3 text-start font-bold">Standard Conversion Formula</th>
                  <th className="py-3 text-end font-bold px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-10 text-gray-400">
                      Loading conversion mappings...
                    </td>
                  </tr>
                ) : filteredConversions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-12">
                      <div className="flex flex-col items-center justify-center">
                        <IconScale size={44} className="text-gray-300 dark:text-zinc-600 mb-2" />
                        <p className="font-semibold text-restro-text">No conversion mappings found</p>
                        <p className="text-xs text-gray-500 max-w-md mt-1">
                          Map bulk units (e.g. 1 Bottle = 750 ml, 1 Case = 9000 ml) to allow automatic packaging calculation during purchase and refills.
                        </p>
                        <button
                          onClick={() => document.getElementById('modal-add-conversion')?.showModal()}
                          className="mt-4 btn btn-sm bg-restro-green hover:bg-restro-green-button-hover text-white border-none"
                        >
                          <IconPlus size={16} /> Add First Conversion
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredConversions.map((c, index) => (
                    <tr key={c.id} className="hover:bg-gray-50/60 dark:hover:bg-zinc-800/40 border-b border-gray-100 dark:border-zinc-800">
                      <td className="px-4 py-3 font-medium text-gray-400">{index + 1}</td>
                      <td className="py-3 font-bold text-restro-text">
                        {c.inventory_item_name || 'Item #' + c.inventory_item_id}
                      </td>
                      <td className="py-3">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                          {c.inventory_unit || 'base unit'}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                          {c.purchase_unit_name} ({c.purchase_unit_symbol})
                        </span>
                      </td>
                      <td className="py-3 font-semibold text-restro-green">
                        1 {c.purchase_unit_name} = {parseFloat(c.conversion_factor)} {c.inventory_unit}
                      </td>
                      <td className="py-3 px-4 text-end">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => openInCalculator(c)}
                            title="Calculate stock with this packaging"
                            className="btn btn-xs rounded-lg border border-restro-border-green bg-restro-gray text-restro-text hover:bg-restro-button-hover flex items-center gap-1 font-medium"
                          >
                            <IconCalculator size={14} /> Calculate
                          </button>
                          <button
                            onClick={() => handleDelete(c.id)}
                            title="Delete conversion mapping"
                            className="btn btn-xs btn-square rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 text-red-600 hover:bg-red-100"
                          >
                            <IconTrash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: INTERACTIVE STOCK TAKE & PACKAGING CALCULATOR */}
      {activeTab === 'calculator' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Input Panel */}
          <div className="lg:col-span-7 bg-white dark:bg-zinc-900 p-6 rounded-2xl border border-restro-border-green shadow-sm flex flex-col gap-5">
            <div>
              <h2 className="font-bold text-lg text-restro-text flex items-center gap-2">
                <IconCalculator className="text-restro-green" size={22} />
                Compound Stock & Packaging Calculator
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Calculate total base stock from full packaging units (bottles, cases, boxes) plus loose/partial quantities.
              </p>
            </div>

            {/* Select Item */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                1. Select Inventory Item
              </label>
              <Select
                options={inventoryItemOptions}
                value={calcItemId ? inventoryItemOptions.find(o => String(o.value) === String(calcItemId)) : null}
                onChange={(selected) => handleCalcItemChange(selected ? selected.value : '')}
                placeholder="Type to search & select inventory item..."
                isClearable
                isSearchable
                menuPosition="fixed"
                styles={selectCustomStyles}
              />
            </div>

            {/* Packaging Unit Selection */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                2. Packaging Unit (Bottle, Box, Case, etc.)
              </label>
              {itemConversions.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-2">
                  {itemConversions.map(c => {
                    const isSelected = String(calcUnitId) === String(c.purchase_unit_id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleCalcUnitChange(String(c.purchase_unit_id))}
                        className={`text-xs px-3 py-1.5 rounded-xl border transition-all font-semibold flex items-center gap-1.5 ${
                          isSelected
                            ? 'bg-restro-green text-white border-restro-green shadow-sm'
                            : 'bg-restro-gray text-restro-text border-restro-border-green hover:bg-restro-button-hover'
                        }`}
                      >
                        <IconPackage size={14} />
                        {c.purchase_unit_name} ({parseFloat(c.conversion_factor)} {c.inventory_unit})
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => handleCalcUnitChange('custom')}
                    className={`text-xs px-3 py-1.5 rounded-xl border transition-all font-semibold ${
                      calcUnitId === 'custom'
                        ? 'bg-restro-green text-white border-restro-green shadow-sm'
                        : 'bg-restro-gray text-restro-text border-restro-border-green hover:bg-restro-button-hover'
                    }`}
                  >
                    Custom Size
                  </button>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <span className="text-xs text-gray-400 block mb-1">Number of Full Units / Containers</span>
                  <input
                    type="number"
                    step="any"
                    value={calcPackCount}
                    onChange={(e) => setCalcPackCount(e.target.value)}
                    placeholder="e.g. 2"
                    className="input input-sm input-bordered w-full rounded-xl border-restro-border-green dark:bg-black text-restro-text font-bold"
                  />
                </div>
                <div>
                  <span className="text-xs text-gray-400 block mb-1">
                    Pack Capacity ({calcBaseUnit} per unit)
                  </span>
                  <input
                    type="number"
                    step="any"
                    value={calcPackSize}
                    onChange={(e) => {
                      setCalcPackSize(e.target.value);
                      if (calcUnitId !== 'custom') setCalcUnitId('custom');
                    }}
                    placeholder="e.g. 750"
                    className="input input-sm input-bordered w-full rounded-xl border-restro-border-green dark:bg-black text-restro-text font-bold"
                  />
                </div>
              </div>
            </div>

            {/* Loose / Remaining Units */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                3. Extra / Loose Remaining Quantity ({calcBaseUnit})
              </label>
              <input
                type="number"
                step="any"
                value={calcLooseQty}
                onChange={(e) => setCalcLooseQty(e.target.value)}
                placeholder="e.g. 600"
                className="input input-sm input-bordered w-full rounded-xl border-restro-border-green dark:bg-black text-restro-text font-bold"
              />
              <span className="text-[11px] text-gray-400 mt-1 block">
                Any opened bottle, loose portions, or loose ml/grams that are not in a full container.
              </span>
            </div>
          </div>

          {/* Result Card */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-zinc-900 dark:to-zinc-800 p-6 rounded-2xl border border-emerald-200 dark:border-emerald-900 shadow-sm flex flex-col justify-between h-full">
              <div>
                <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 font-bold mb-3">
                  <IconSparkles size={20} />
                  <span>Calculated Inventory Stock</span>
                </div>

                <div className="space-y-3 bg-white dark:bg-black/40 p-4 rounded-xl border border-emerald-100 dark:border-zinc-800">
                  <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-300">
                    <span>Full Containers:</span>
                    <span className="font-semibold">{packCountNum} × {packSizeNum} {calcBaseUnit} = <strong>{fullContainersBaseQty} {calcBaseUnit}</strong></span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-300">
                    <span>Loose / Partial:</span>
                    <span className="font-semibold">+ <strong>{looseQtyNum} {calcBaseUnit}</strong></span>
                  </div>
                  <div className="border-t border-dashed border-gray-200 dark:border-zinc-700 pt-2 flex justify-between items-center">
                    <span className="font-bold text-sm text-restro-text">Total Base Stock:</span>
                    <span className="text-xl font-extrabold text-restro-green">
                      {calculatedTotalStock} {calcBaseUnit}
                    </span>
                  </div>
                </div>

                {calcItemObj && (
                  <div className="mt-4 p-3 rounded-xl bg-white dark:bg-black/30 border border-gray-200 dark:border-zinc-800 text-xs">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-gray-500">Current Item Stock:</span>
                      <span className={`font-bold ${parseFloat(calcItemObj.quantity || 0) < 0 ? 'text-red-600' : 'text-restro-text'}`}>
                        {calcItemObj.quantity || 0} {calcBaseUnit}
                        {parseFloat(calcItemObj.quantity || 0) < 0 && " (Deficit)"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-emerald-700 dark:text-emerald-400 font-bold pt-1 border-t border-gray-100 dark:border-zinc-800">
                      <span>After Refill (+{calculatedTotalStock}):</span>
                      <span>
                        {(parseFloat(calcItemObj.quantity || 0) + calculatedTotalStock).toFixed(2)} {calcBaseUnit}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  disabled={!calcItemId || calculatedTotalStock <= 0 || isRecordingStock}
                  onClick={handleRecordStockRefill}
                  className="w-full btn bg-restro-green hover:bg-restro-green-button-hover text-white font-bold rounded-xl border-none shadow-sm disabled:opacity-50"
                >
                  {isRecordingStock ? 'Recording...' : `Refill +${calculatedTotalStock} ${calcBaseUnit} to Inventory`}
                </button>
                <p className="text-[11px] text-gray-400 text-center mt-2">
                  This will record an 'IN' stock movement and automatically reconcile any negative deficit balance.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: ADD CONVERSION MAPPING */}
      <dialog id="modal-add-conversion" className="modal">
        <div className="modal-box border border-restro-border-green dark:bg-zinc-900 rounded-2xl max-w-lg overflow-visible" style={{ overflow: 'visible' }}>
          <h3 className="font-bold text-lg mb-2 text-restro-text flex items-center gap-2">
            <IconScale size={20} className="text-restro-green" />
            Add Packaging Unit Conversion
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Map an item to a bulk packaging unit (e.g. 1 Bottle = 750 ml, 1 Case = 12 Bottles).
          </p>

          <form onSubmit={handleCreate}>
            {/* Inventory Item */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Inventory Item
              </label>
              <Select
                options={inventoryItemOptions}
                value={inventoryItemId ? inventoryItemOptions.find(o => String(o.value) === String(inventoryItemId)) : null}
                onChange={selected => setInventoryItemId(selected ? selected.value : '')}
                placeholder="Type to search & select inventory item..."
                isClearable
                isSearchable
                menuPosition="fixed"
                styles={selectCustomStyles}
              />
              {selectedItemObj && (
                <span className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-1 block font-medium">
                  ✓ Base tracking unit for {selectedItemObj.title} is <strong>{selectedItemObj.unit}</strong>
                </span>
              )}
            </div>

            {/* Purchase / Packaging Unit */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Packaging / Purchase Unit
              </label>
              <Select
                options={purchaseUnitOptions}
                value={purchaseUnitId ? purchaseUnitOptions.find(o => String(o.value) === String(purchaseUnitId)) : null}
                onChange={selected => setPurchaseUnitId(selected ? selected.value : '')}
                placeholder="Type to search & select packaging unit..."
                isClearable
                isSearchable
                menuPosition="fixed"
                styles={selectCustomStyles}
              />

              {/* Quick Select Popular Units */}
              <div className="mt-2">
                <span className="text-[11px] text-gray-400 block mb-1">Or Quick Select:</span>
                <div className="flex flex-wrap gap-1.5">
                  {purchaseUnits.slice(0, 8).map(pu => (
                    <button
                      key={pu.id}
                      type="button"
                      onClick={() => setPurchaseUnitId(String(pu.id))}
                      className={`text-xs px-2.5 py-1 rounded-lg border font-medium transition-all ${
                        String(purchaseUnitId) === String(pu.id)
                          ? 'bg-restro-green text-white border-restro-green shadow-sm'
                          : 'bg-gray-50 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-zinc-700 hover:bg-gray-100 dark:hover:bg-zinc-700'
                      }`}
                    >
                      {pu.name} ({pu.symbol})
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Conversion Factor */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Conversion Factor ({selectedItemObj?.unit || 'Base Units'} in 1 Packaging Unit)
              </label>
              <input
                type="number"
                step="any"
                className="input input-sm input-bordered w-full rounded-xl border-restro-border-green dark:bg-black text-restro-text font-bold"
                placeholder="e.g. 750 (for a 750ml bottle) or 9000 (for a 12-bottle case)"
                value={conversionFactor}
                onChange={e => setConversionFactor(e.target.value)}
                required
              />
              <span className="text-[11px] text-gray-400 mt-1 block">
                How many {selectedItemObj?.unit || 'base units'} are in 1 {selectedUnitObj?.name || 'packaging unit'}?
              </span>
            </div>

            {/* Live Formula Preview */}
            {selectedItemObj && selectedUnitObj && conversionFactor && parseFloat(conversionFactor) > 0 && (
              <div className="p-3 mb-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-800 dark:text-emerald-200">
                <span className="font-bold">Formula Preview:</span>
                <p className="mt-0.5 text-sm font-extrabold">
                  1 {selectedUnitObj.name} ({selectedUnitObj.symbol}) = {parseFloat(conversionFactor)} {selectedItemObj.unit}
                </p>
              </div>
            )}

            <div className="modal-action">
              <button
                type="button"
                className="btn btn-sm btn-ghost rounded-xl"
                onClick={() => document.getElementById('modal-add-conversion')?.close()}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-sm bg-restro-green hover:bg-restro-green-button-hover text-white rounded-xl border-none font-bold"
              >
                {isSubmitting ? 'Saving...' : 'Save Conversion'}
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </Page>
  );
}
